import {Component, ConfigOptions} from 'panel';

import {ENTER_KEY_CODE} from 'common/util/dom';
import {isValidEmailAddress} from 'common/util/string';

import {track, TrackEvents, TrackProperties} from '../tracking';
import template from './index.jade';
import './index.styl';

interface RequestAccessState {
  email: string;
  fullName: string;
  stage: string;
  emailError: string;
  nameError: string;
  loggedIn: boolean;
  nextUrl: string;
}

export class RequestAccess extends Component<RequestAccessState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'next-url': {type: `string`, description: `project link to be used if a redirect is needed`},
      'recaptcha-key': {type: `string`, description: `secret key for recaptcha`},
      'full-name': {type: `string`, description: `user name`},
      email: {type: `string`, description: `user email address`},
      'logged-in': {type: `boolean`, description: `boolean value to indicate if user is logged in, defaults to false`},
      stage: {type: `string`, description: `stage in request access process`},
      'project-id': {type: `string`, description: `project id`},
      'invite-error': {type: `string`, description: `error with an expired or nonexistent invite`},
      'inviter-email': {type: `string`, description: `the inviter email for invite errors`},
      'help-link': {type: `string`, description: `custom link to give help to users`},
      'help-message': {type: `string`, description: `message to display before the help button`},
    };
  }
  get config(): ConfigOptions<RequestAccessState> {
    return {
      template,
      defaultState: {
        email: ``,
        fullName: ``,
        stage: `input`,
        emailError: ``,
        nameError: ``,
        loggedIn: false,
        nextUrl: ``,
      },
      helpers: {
        expiredRequest: () => {
          this.update({stage: `input`});
        },
        getSecondaryButtonLink: () => {
          const loginURL = `/login/?next=${this.state.nextUrl}`;
          return this.state.loggedIn ? `/logout/?next=${encodeURIComponent(loginURL)}` : loginURL;
        },
        handleNameInput: (ev) => {
          const name = ev.target.value;
          this.update({fullName: name});
          if (this.state.nameError) {
            this.validateFullName();
          }
        },
        handleEmailInput: (ev) => {
          const email = ev.target.value;
          if (this.state.emailError) {
            this.validateEmail();
          }
          this.update({email});
        },
        handleKeydown: (ev) => {
          if (ev.keyCode === ENTER_KEY_CODE) {
            this.helpers.submitRequest();
          }
        },
        submitRequest: () => {
          this.validateFullName();
          if (this.validateEmail() && this.validateFullName()) {
            window[`grecaptcha`].execute();
          }
        },
      },
    };
  }
  connectedCallback() {
    super.connectedCallback();
    window[`handleSuccessfulRecaptchaResponse`] = this.submitRequestAccess.bind(this);
    const stateUpdate: Partial<RequestAccessState> = {};

    if (this.attr(`stage`)) {
      stateUpdate.stage = this.attr(`stage`);
    }

    if (this.attr(`next-url`)) {
      stateUpdate.nextUrl = this.attr(`next-url`);
      if (!stateUpdate.nextUrl.includes(`#`) && window.location?.hash) {
        stateUpdate.nextUrl += encodeURIComponent(window.location.hash.replace(/#+/g, `#`).replace(/#$/, ``));
      }
    }

    if (this.attr(`logged-in`)) {
      stateUpdate.loggedIn = this.attr(`logged-in`);
      stateUpdate.email = this.attr(`email`);
      stateUpdate.fullName = this.attr(`full-name`);
    }

    if (this.attr(`invite-error`)) {
      stateUpdate.stage = this.attr(`invite-error`) === `expired` ? `invite-expired` : `invite-invalid`;
    }

    this.update({...stateUpdate});
    track(TrackEvents.ViewRequestAccessPage, this.getTrackingProperties());
  }
  validateFullName() {
    const valid = this.state.fullName !== ``;
    this.update({nameError: valid ? `valid` : `invalid`});
    return valid;
  }
  validateEmail() {
    const valid = isValidEmailAddress(this.state.email);
    this.update({emailError: valid ? `valid` : `invalid`});
    return valid;
  }
  submitRequestAccess() {
    const requestForm: any = document.getElementById(`mpRequestAccess`);
    requestForm.submit();
  }
  getTrackingProperties() {
    const trackProps = {
      [TrackProperties.ProjectID]: this.attr(`project-id`),
      [TrackProperties.IsLoggedIN]: this.attr(`logged-in`),
      [TrackProperties.RequestStage]: this.state.stage,
      [TrackProperties.NextURL]: this.attr(`next-url`),
    };

    if (this.attr(`help-link`)) {
      trackProps[TrackProperties.AccessInstructionsLink] = this.attr(`help-link`);
    }

    if (this.state.email) {
      trackProps[TrackProperties.Email] = this.state.email;
    }
    return trackProps;
  }
}
customElements.define(`mp-request-access`, RequestAccess);
